import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { ODataList } from '../entities/academy-entities.model';
import { ODataQuery } from '../entities/academy-query.model';
import {
  TenantBrandingCreateRequest,
  TenantBrandingResponse,
} from '../entities/academy.model';
import { BrandingResponse } from '../entities/academy-responses.model';
import { AcademyCrudService } from './academy-crud.service';
import { AcademyODataService } from './academy-odata.service';

/**
 * HTTP client for the white-label branding area. The anonymous host resolution
 * (`GET /branding`) runs before authentication; tenant-branding reads go through the
 * read-only OData helper (`AcademyODataService.list`) and writes through
 * `AcademyCrudService` over the `TenantBrandings` entity set.
 * Source of truth: `docs/academy-api-integration.md` §7.x.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly http = inject(HttpClient);
  private readonly odata = inject(AcademyODataService);
  private readonly crud = inject(AcademyCrudService);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `GET /branding` — get the white-label branding for the calling **host**
   * (the server matches the request's Host/subdomain to a tenant branding).
   * ANONYMOUS: runs before authentication, so it MUST NOT send credentials.
   * No host match yields `{ IsDefault: true }`.
   */
  public getHostBranding(): Observable<BrandingResponse> {
    return this.http.get<BrandingResponse>(`${this.baseUrl}/branding`);
  }

  /** `OPTIONS /TenantBrandings` — OData list of tenant brandings (value envelope). */
  public listTenantBrandings(
    query: ODataQuery = {}
  ): Observable<ODataList<TenantBrandingResponse>> {
    return this.odata.list<TenantBrandingResponse>('TenantBrandings', query);
  }

  /** `POST /TenantBrandings` — create a tenant branding (`TenantBrandingId` is server-assigned). */
  public createTenantBranding(
    viewModel: TenantBrandingCreateRequest
  ): Observable<TenantBrandingResponse> {
    return this.crud.create<TenantBrandingResponse>(
      'TenantBrandings',
      viewModel
    );
  }
}
